import { Component, Input, OnInit } from '@angular/core';
import EmailRecipientTypes = Boo.Objects.Enums.EmailRecipientTypes;
import CustomerUser = Boo.Objects.CustomerUser;
import { CustomerUserService } from '../../../services/customer-user.service'
import { UserService } from '../../../services/user.service';
import { EmailRecipientTypePipe } from '../../../shared/pipes/emailRecipientType.pipe';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import CustomValidators from '../../../shared/custom-validators';
import { Observable, of, switchMap, finalize, filter, forkJoin } from 'rxjs';
import { ConfirmBox } from '../../shared/ConfirmBox/confirm-box';

@Component({
  selector: 'app-components-managecustomer-customeruser',
  templateUrl: './CustomerUser.component.html',
  providers: [EmailRecipientTypePipe]
})
export class CustomerUserComponent implements OnInit {
  @Input() customer: Boo.Objects.Customer;
  @Input() partner: Boo.Objects.Partner;
  @Input() customerUser: CustomerUser;
  @Input() countryAbbreviation: string;
  @Input() unavailableUsernames: string[];

  isLoading: boolean;
  emailRecipientOptions = [
    EmailRecipientTypes.To,
    EmailRecipientTypes.Cc,
    EmailRecipientTypes.Bcc
  ]
  customerUserForm = this.formBuilder.group({
    FirstName: ['', Validators.required],
    LastName: ['', Validators.required],
    Username: ['', [Validators.required, Validators.email]],
    EmailRecipientType: this.formBuilder.control<EmailRecipientTypes | null>(null),
    CanReceiveTextMessages: this.formBuilder.control<boolean>(false),
    PhoneNumbers: this.formBuilder.array<FormGroup<{
      PhoneNumber: FormControl<string>;
      CustomerUserPhoneNumberId: FormControl<number>;
      Primary: FormControl<boolean>;
    }>>([])
  });

  get phoneNumbers(): FormArray {
    return this.customerUserForm.get('PhoneNumbers') as FormArray;
  }

  constructor(
    private customerUserService: CustomerUserService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private activeModal: NgbActiveModal,
    private confirmBox: ConfirmBox
  ) { };

  ngOnInit(): void {
    this.isLoading = true;
    this.customerUserForm.get('CanReceiveTextMessages').setValue(this.customerUser.CanReceiveTextMessages);

    if (!this.isNewUser()) {
      this.customerUserForm.get('FirstName').setValue(this.customerUser.FirstName);
      this.customerUserForm.get('LastName').setValue(this.customerUser.LastName);
      this.customerUserForm.get('Username').setValue(this.customerUser.Username);
      this.customerUserForm.get('EmailRecipientType').setValue(this.customerUser.EmailRecipientType);
    } else {
      this.customerUserForm.get('EmailRecipientType').setValue(EmailRecipientTypes.To);
    }

    if (this.customerUser.IsPrimary) {
      this.customerUserForm.get('EmailRecipientType').disable();
    }

    const phoneNumbers = this.customerUser.CustomerUserPhoneNumbers.length > 0
      ? [...this.customerUser.CustomerUserPhoneNumbers]
      : [{ PhoneNumber: { Number: '', Primary: true } } as Boo.Objects.CustomerUserPhoneNumber];

    phoneNumbers
      .sort((a, b) => a.PhoneNumber.Primary && b.PhoneNumber.Primary ? 0 : a.PhoneNumber.Primary ? -1 : 1)
      .map(p =>
        this.formBuilder.group({
          PhoneNumber: [p.PhoneNumber.Number, [
            CustomValidators.requiredIfValidator(() => p.PhoneNumber.Primary && !this.isDashboardOnlyUser() && (this.customerUser.IsPrimary || (this.partner.AllowsTextMessaging && this.customerUserForm.get('CanReceiveTextMessages').value))),
            CustomValidators.phoneNumberValidator(() => this.countryAbbreviation)
          ]],
          CustomerUserPhoneNumberId: p.CustomerUserPhoneNumberId,
          Primary: p.PhoneNumber.Primary
        })
      ).forEach(fg => (this.customerUserForm.get('PhoneNumbers') as FormArray).push(fg));

    this.isLoading = false;
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  onCanReceiveTextMessagesValueChange(canReceiveTextMessages: boolean): void {
    this.customerUserForm.get('CanReceiveTextMessages').setValue(canReceiveTextMessages);
    this.phoneNumbers.controls.forEach(c => c.get('PhoneNumber').updateValueAndValidity());
  }

  canConfigureTextMessaging(): boolean {
    return this.partner.AllowsTextMessaging
      && this.customerUser.CanContact
      && this.customerUser.IsCustomerUserActive
      && (this.customerUserForm.get('CanReceiveTextMessages').value || this.hasPrimaryPhoneNumberValue());
  }

  isNewUser() {
    return this.customerUser.CustomerUserId === 0;
  }

  isDashboardOnlyUser(): boolean {
    return !this.customerUser?.CanContact;
  }

  save(): void {
    if (this.customerUserForm.invalid) {
      this.customerUserForm.markAllAsTouched();
      this.customerUserForm.markAsDirty();
      this.customerUserForm.updateValueAndValidity();
      toastr.error('Please fix the validation errors and try again.');
      return;
    }

    if (this.isNewUser()) {
      this.saveNewUser();
    }
    else {
      this.saveExistingUser();
    }
  }

  private saveExistingUser(): void {
    this.isLoading = true;
    this.confirmUserOverwrite().pipe(
      switchMap(() => this.confirmUserEdit()),
      filter(confirmed => confirmed),
      switchMap(() => {
        this.copyFormToCustomerUser();
        return this.saveCustomerUser();
      }),
      finalize(() => this.isLoading = false),
    ).subscribe({
      next: () => {
        toastr.success('User saved successfully.');
        this.activeModal.close();
      },
      error: (error) => {
        if (error.message !== 'User Rejected.') {
          toastr.error(error.message);
        }
      }
    });
  }

  private saveNewUser(): void {
    this.isLoading = true;
    this.userService.getByUsername(this.customerUserForm.get('Username').value)
      .pipe(
        switchMap(existingUser => forkJoin([
          of(existingUser),
          existingUser ? this.customerUserService.getByIds([existingUser?.UserId]) : of([])])),
        switchMap(([existingUser, customerUsers]) => {
          const existingCustomerUser = customerUsers?.find(cu => cu.CustomerId === this.customer.CustomerId);

          if (!existingUser) {
            this.copyFormToCustomerUser();
            return this.createCustomerUser();
          }
          else if (!existingCustomerUser) {
            this.customerUserForm.get('FirstName').setValue(existingUser.FirstName);
            this.customerUserForm.get('LastName').setValue(existingUser.LastName);
            
            toastr.info(`A user with the email ${existingUser.Username} already exists.<br><br>The first and last name have been updated to match the existing user.`);

            this.copyFormToCustomerUser();
            return this.createCustomerUser();
          }
          else if (this.customerUser.CanContact && !existingCustomerUser.CanContact) {
            this.customerUserForm.get('FirstName').setValue(existingUser.FirstName);
            this.customerUserForm.get('LastName').setValue(existingUser.LastName);
            this.customerUser.CanContact = true;
            this.customerUser.CustomerUserId = existingCustomerUser.CustomerUserId;
            this.customerUser.CustomerUsers = customerUsers;

            toastr.info(`A reporting dashboard user with the email ${existingUser.Username} already exists for this customer.<br><br>The first and last name have been updated to match the existing user.`);

            this.copyFormToCustomerUser();
            return this.saveCustomerUser()
              .pipe(switchMap(() => of(this.customerUser)));
          }
          else {
            throw this.customerUser.CanContact ? new Error(`A contact with the email ${existingUser.Username} already exists for this customer.`)
              : new Error(`A user with the email ${existingUser.Username} already has access to the customer's dashboard.`);
          }
        }),
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: (customerUser: CustomerUser | void) => {
          toastr.success('Successfully created new user.');

          if (customerUser) {
            this.activeModal.close(customerUser);
            return;
          }

          this.activeModal.close();
        },
        error: (error) => toastr.error(error)
      });
  }

  private hasPrimaryPhoneNumberValue(): boolean {
    const primaryPhone = this.phoneNumbers.controls.find(c => c.get('Primary').value);
    return !!primaryPhone?.get('PhoneNumber').value?.trim();
  }

  private confirmUserEdit(): Observable<boolean> {
    if (this.customerUser.CustomerUsers.length <= 1) {
      return of(true);
    }

    if (!this.userDataModified()) {
      return of(true);
    }

    const msg = 'Warning: If you click \'OK\', you will overwrite information for a user that has access to several accounts. The user will be updated as ' + this.customerUser.FullName + '<br><br>' +
      'Did you mean to ADD a new user to this account instead? If so, please click \'CANCEL\' and then ADD a new user.' + '<br><br>' +
      'Unwanted users may be deleted from an account by clicking on the garbage can icon.';
    return this.confirmBox.confirm(msg);
  }

  private confirmUserOverwrite(): Observable<void> {
    const username = this.customerUserForm.get('Username').value;

    if (!this.usernameHasChanged()) {
      return of(null);
    }

    return this.userService.getByUsername(username)
      .pipe(switchMap(user => {
        if (user == null) {
          return of(null);
        }
        else {
          throw new Error(`There is already a user with this username: ${user.Username}
                                  <br><br>Editing a user to the same username as an existing user is not allowed. Please add a new user instead.`);
        }
      }));
  }

  private copyFormToCustomerUser(): void {
    this.customerUser.FirstName = this.customerUserForm.get('FirstName').value;
    this.customerUser.LastName = this.customerUserForm.get('LastName').value;
    this.customerUser.Username = this.customerUserForm.get('Username').value;
    this.customerUser.EmailRecipientType = this.customerUserForm.get('EmailRecipientType').value;
    this.customerUser.CanReceiveTextMessages = this.customerUserForm.get('CanReceiveTextMessages').value;

    const phoneNumbers: Boo.Objects.CustomerUserPhoneNumber[] = [];
    this.customerUserForm.get('PhoneNumbers').value.forEach(pn => {
      if (pn.Primary) {
        this.customerUser.PrimaryPhoneNumber = pn.PhoneNumber;
      }

      if (!pn.PhoneNumber?.trim()) {
        return;
      }

      const existingPhoneNumber = this.customerUser.CustomerUserPhoneNumbers.find(p => p.CustomerUserPhoneNumberId === pn.CustomerUserPhoneNumberId);
      if (existingPhoneNumber) {
        existingPhoneNumber.PhoneNumber.Number = pn.PhoneNumber;
        existingPhoneNumber.PhoneNumber.Primary = pn.Primary;
        phoneNumbers.push(existingPhoneNumber);
        return;
      }

      phoneNumbers.push({
        PhoneNumber: {
          Number: pn.PhoneNumber,
          Primary: pn.Primary
        }
      } as Boo.Objects.CustomerUserPhoneNumber);
    });
    this.customerUser.CustomerUserPhoneNumbers = phoneNumbers;
  }

  private saveCustomerUser(): Observable<void> {
    return this.userService.save(
      this.customerUser,
      this.customerUser.CustomerId,
      this.customerUser.IsCustomerUserActive,
      this.customerUser.IsPrimary,
      this.customerUser.CanContact,
      this.customerUser.CanReceiveTextMessages,
      this.customerUser.CustomerUserPhoneNumbers,
      this.customerUser.EmailRecipientType
    );
  }

  private createCustomerUser(): Observable<CustomerUser> {
    return this.customerUserService
      .create(
        this.customer,
        this.customerUser.Username,
        this.customerUser.FirstName,
        this.customerUser.LastName,
        this.customerUser.IsCustomerUserActive,
        this.customerUser.CanContact,
        this.customerUser.CanReceiveTextMessages,
        this.customerUser.CustomerUserPhoneNumbers,
        this.customerUser.EmailRecipientType)
      .pipe(
        switchMap(customerUser => forkJoin([of(customerUser), this.customerUserService.getByIds([customerUser.UserId])])),
        switchMap(([customerUser, customerUsers]) => {
          this.customerUser.CustomerUserId = customerUser.CustomerUserId;
          this.customerUser.UserId = customerUser.UserId;
          this.customerUser.CustomerUserPhoneNumbers = customerUser.CustomerUserPhoneNumbers;
          this.customerUser.CustomerUsers = customerUsers;
          return of(this.customerUser);
        })
      );
  }

  private usernameHasChanged() {
    return this.customerUser.Username !== this.customerUserForm.get('Username').value;
  }

  private userDataModified() {
    return this.customerUser.FirstName !== this.customerUserForm.get('FirstName').value
      || this.customerUser.LastName !== this.customerUserForm.get('LastName').value
      || this.customerUser.Username !== this.customerUserForm.get('Username').value;
  }
}
